import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";

/**
 * Live queue board.
 *
 * Primary transport is Server-Sent Events (`/api/queue/stream`) so the doctor and
 * reception screens update the instant anything changes. If the stream cannot be
 * established (proxy buffering, offline, tab restored from sleep) we silently fall
 * back to polling `/api/queue/version` and refetching the board on change.
 */
export default function useQueueBoard({ scope = "doctor", doctorId = null, enabled = true }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const esRef = useRef(null);
  const versionRef = useRef(-1);

  const params = {};
  if (scope) params.scope = scope;
  if (doctorId) params.doctor_id = doctorId;

  const fetchBoard = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/queue/board", { params });
      versionRef.current = data.version;
      setBoard(data);
    } catch (e) {
      // keep last known board on transient failures
    } finally {
      setLoading(false);
    }
  }, [enabled, scope, doctorId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE subscription
  useEffect(() => {
    if (!enabled) return undefined;
    setLoading(true);
    fetchBoard();

    const qs = new URLSearchParams(params).toString();
    let es;
    try {
      es = new EventSource(`${API}/queue/stream?${qs}`, { withCredentials: true });
    } catch (e) {
      es = null;
    }
    if (es) {
      esRef.current = es;
      es.addEventListener("board", (evt) => {
        try {
          const data = JSON.parse(evt.data);
          versionRef.current = data.version;
          setBoard(data);
          setLoading(false);
          setLive(true);
        } catch (e) {
          /* ignore malformed frame */
        }
      });
      es.onerror = () => setLive(false);
    }

    // Polling safety net — cheap version check, refetch only when it moved
    const poll = setInterval(async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get("/queue/version");
        if (data.version !== versionRef.current) await fetchBoard();
      } catch (e) {
        /* offline; next tick retries */
      }
    }, 6000);

    const onVisible = () => { if (!document.hidden) fetchBoard(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisible);
      if (es) es.close();
      esRef.current = null;
    };
  }, [enabled, scope, doctorId]); // eslint-disable-line react-hooks/exhaustive-deps

  return { board, loading, live, refresh: fetchBoard };
}
